import { IService, IServiceCategory } from 'features/cashier/services/types/api';
import { SERVICE_ITEM_TYPE } from 'features/cashier/services/types/constant';
import { IBillDetailData } from 'features/payment/services/types/bill';
import { get, groupBy, set, sumBy } from 'lodash';
import { COMBO_SERVICES_DATA } from 'services/shop/constants';
import { ICustomerItemData } from 'services/shop/types/customer';
import { IDiscountSettingData } from 'services/shop/types/discount';
import uuid from 'utils/uuid';

export const NAME_REDUCER = 'cashier';

export const PREFIX_ACTIONS = 'cashier_feature_';

export const PATH_LOADING = {
  getDetail: `loading.${NAME_REDUCER}.getDetail`,
  getCategories: `loading.${NAME_REDUCER}.getCategories`,
  getStaffList: `loading.${NAME_REDUCER}.getStaffList`,
  getCustomerList: `loading.${NAME_REDUCER}.getCustomerList`,
};

export const FIELD_KEYS = {
  DURATION: 'duration',
  PRICECOST: 'priceCost',
  PRICESELL: 'priceSell',
  COMMISSION: 'commission'
} as const;

export type FieldKeys = typeof FIELD_KEYS[keyof typeof FIELD_KEYS];

const SymbolDraff = '/';

export const encodeDraftServiceSwitchStaffId = (data: { staffId: string, itemId: string }) => {
  if (!data) return '';
  const { itemId, staffId } = data;
  return staffId + SymbolDraff + itemId;
};

export const decodeDraftServiceSwitchStaffId = (draftValue: string) => {
  if (!draftValue) return null;
  const [staffId, itemId] = draftValue.split(SymbolDraff);

  return ({
    staffId,
    itemId,
  });
};

export enum ITEM_HANDLE_MODE {
  DISCOUNT_ITEM = 'DISCOUNT_ITEM',
  SUPPLY_FEE = 'SUPPLY_FEE',
  DELETE_ITEM = 'DELETE_ITEM',
}

export enum FEATURE_PRODUCT_MODE {
  SERVICE = 'SERVICE',
  PRODUCT = 'PRODUCT',
}

export const QUICK_PAY_ID = 'QUICK_PAY_ID';

/** Nguon cua discount ticket do phan thuong stamp loyalty dat vao */
export const STAMP_REWARD_DISCOUNT_SOURCE = 'STAMP_REWARD';

export enum DiscountType {
  PERCENT = 'PERCENT',
  MONEY = 'MONEY'
}

export const UNKNOWN_CUSTOMER: ICustomerItemData = {
  email: '',
  id: '',
  imageAvatarUrl: '',
  name: '',
  numberVisit: 0,
  phone: '',
  point: 0,
  status: ''
};

export const QUICK_PAY_NAME = 'Service Amount';

// @ts-ignore
export const parseDecimal = (num: number | undefined | null) => +((+(num || 0)?.toString()?.match(/^-?\d+(?:\.\d{0,2})?/)?.[0])?.toFixed(2) || 0);

export const COMBO_PACKAGE_CASHIER_DATA: Record<string, {
  services: IService[],
  data?: IServiceCategory,
}> = {};

export const saveComboData = (comboId: string, data: IServiceCategory, services: IService[]) => {
  set(COMBO_PACKAGE_CASHIER_DATA, comboId, {
    services,
    data,
  });
};
export const updateServiceComboData = (comboId: string, services: IService[]) => {
  const comboData = get(COMBO_PACKAGE_CASHIER_DATA, comboId);
  if (!comboData) return;
  comboData.services = services.map(ser => ({
    ...ser,
  }));
};

export const getComboIdFromSerUUID = (serUUID: string) => {
  const [comboId] = serUUID.split('_@_');
  return comboId || '';
};

export const removeSerItemByUUID = (serUUID: string) => {
  const comboId = getComboIdFromSerUUID(serUUID);
  if (!comboId) return;

  const comboData = get(COMBO_PACKAGE_CASHIER_DATA, comboId);
  if (!comboData) return;

  const services = comboData.services.filter(s => s.uuid_local !== serUUID);
  if (services.length === 1) {
    delete COMBO_PACKAGE_CASHIER_DATA[comboId];
    return;
  }
  set(COMBO_PACKAGE_CASHIER_DATA, comboId, {
    ...comboData,
    services,
  });
};

export const savedComboDataWithBillDetail = (data: IBillDetailData | null) => {
  if (!data) return;
  const groupByValue = data?.items.map(staff => {
    return groupBy(staff.services.filter(o => o.clientId?.includes('_@_')), s => getComboIdFromSerUUID(s.clientId || ''));
  });
  groupByValue?.map(staffGroupObjectValue => {
    Object.entries(staffGroupObjectValue).map(([comboId, services]) => {
      const [_staffId, id] = comboId.split('_');
      const comboData = get(COMBO_SERVICES_DATA, id);
      saveComboData(comboId, comboData, services.map(s => {
        const result: IService = {
          discount: s.discount,
          duration: s.duration,
          itemId: s.itemId,
          itemName: s.itemName,
          itemType: s.itemType as keyof typeof SERVICE_ITEM_TYPE,
          price: s.price,
          note: '',
          clientId: s.clientId,
          uuid_local: s.clientId,
        };
        return result;
      }));
    });
  });
};
export type IComboCalcData = {
  price: number;
  discount: number;
  data?: IServiceCategory | undefined;
  services: IService[];
  finalPrice: number;
};
export const getComboCalcData = (comboId: string): IComboCalcData | null => {
  const comboData = get(COMBO_PACKAGE_CASHIER_DATA, comboId);
  if (!comboData) return null;
  const price = sumBy(comboData.services, ser => ser.price);
  const discount = sumBy(comboData.services, ser => {
    const discount = ser.discountType === DiscountType.PERCENT ? ((ser.price ?? 0) * ser.discount / 100) : ser.discount ?? 0;
    return discount;
  });
  const comboAfterDiscountPrice = price - discount;

  return ({
    price, discount, data: comboData.data,
    services: comboData.services,
    finalPrice: comboAfterDiscountPrice,
  });
};

export const mappingChildComboToService = (staffId: string, data: IServiceCategory, discountSetting: IDiscountSettingData | null): IService[] => {
  const comboUUID = `${staffId}_${data.id}_${uuid()}`;
  const selectedServices: IService[] = data?.childServices?.map(o => {
    const ser: IService = {
      itemId: o.id,
      itemName: o.serviceName,
      itemType: SERVICE_ITEM_TYPE.SERVICE,
      price: o.priceCost || o.priceSell || 0,
      discount: 0,
      note: null,
      duration: o.duration,
      discountType: DiscountType.MONEY,
      uuid_local: `${comboUUID}_@_${uuid()}`,
      supplyFee: o.supplyFee || 0,
    };
    if (o.discountValue) {
      set(ser, 'discount', o.discountValue);
      set(ser, 'discountType', o.discountType);
      set(ser, 'note', '');
      set(ser, 'discountSetting', { ownerPaidPercent: discountSetting?.discountOwnerItem || 0, staffPaidPercent: discountSetting?.discountStaffItem || 100 });
    }
    return ser;
  }) || [];
  saveComboData(comboUUID, data, selectedServices);
  return selectedServices;
};

export const buildServiceFromCategory = (data: IServiceCategory, discountSettingConfig: IDiscountSettingData | null): IService => {
  const ser: IService = {
    itemId: data.id,
    itemName: data.serviceName,
    itemType: SERVICE_ITEM_TYPE.SERVICE,
    price: data.priceCost || data.priceSell || 0,
    discount: 0,
    note: null,
    duration: data.duration,
    discountType: DiscountType.MONEY,
    uuid_local: uuid(),
    supplyFee: data.supplyFee || 0,
  };
  if (data.discountValue) {
    set(ser, 'discount', data.discountValue);
    set(ser, 'discountType', data.discountType);
    set(ser, 'note', '');
    set(ser, 'discountSetting', { ownerPaidPercent: 0, staffPaidPercent: 100 });
    if (discountSettingConfig) {
      set(ser, 'discountSetting', { ownerPaidPercent: discountSettingConfig?.discountOwnerItem || 0, staffPaidPercent: discountSettingConfig?.discountStaffItem || 0 });
    }
  }
  return ser;
};

export enum TYPE_GET_INFO_GIFT {
  CHECK_BALANCE = 'CHECK_BALANCE',
  ISSUE = 'ISSUE',
  REDEEM = 'REDEEM',
}
